import { useEffect } from "react";
import Cookies from "universal-cookie";
import * as Sentry from "@sentry/react";
import { useApp } from "./app-provider";

export const FINGERPRINT_COOKIE_NAME = "coff_fp";
export const FINGERPRINT_MAX_AGE = 60 * 60 * 24 * 365; // 1 year in seconds

export function getOrCreateFingerprint(
	cookieJar: Cookies = new Cookies(),
): string {
	const existing = cookieJar.get(FINGERPRINT_COOKIE_NAME);
	if (existing) return existing;

	const fingerprint = crypto.randomUUID();
	cookieJar.set(FINGERPRINT_COOKIE_NAME, fingerprint, {
		maxAge: FINGERPRINT_MAX_AGE,
		path: "/",
		sameSite: "lax",
	});
	return fingerprint;
}

/**
 * Checks the current URL for a `ref` query param and, if present,
 * records the referral visit via the provided callback, then cleans the URL.
 *
 * Returns true if a referral was detected, false otherwise.
 */
export function processReferral(
	location: { search: string; pathname: string; hash: string },
	userAgent: string,
	recordFn: (
		code: string,
		fingerprint: string,
		landingPage: string,
		userAgent: string,
	) => Promise<unknown>,
	cookieJar?: Cookies,
): boolean {
	const params = new URLSearchParams(location.search);
	const referralCode = params.get("ref");

	if (!referralCode) return false;

	const fingerprint = getOrCreateFingerprint(cookieJar);

	recordFn(referralCode, fingerprint, location.pathname, userAgent).catch(
		(error) => {
			Sentry.captureException(error);
		},
	);

	// Remove ref param from URL to keep it clean
	params.delete("ref");
	const newSearch = params.toString();
	const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ""}${location.hash}`;
	window.history.replaceState({}, "", newUrl);

	return true;
}

export default function ReferralProvider({
	children,
}: {
	children: React.ReactNode;
}) {
	const { appManager } = useApp();

	useEffect(() => {
		processReferral(
			window.location,
			navigator.userAgent,
			appManager.recordReferral,
		);
	}, [appManager.recordReferral]);

	return <>{children}</>;
}
